// src/pages/Invoices.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Filter,
    Plus,
    Loader2,
    Users,
    ChevronRight,
    ChevronLeft,
    Trophy,
    Medal,
    Award,
    Crown,
    Sparkles,
} from 'lucide-react';
import PeriodPicker from '@/components/PeriodPicker';
import LocationSelector from '@/components/LocationSelector';
import { toast } from 'sonner';

// مهم: مسیر دیتای لوکیشن
import { locationData } from '@/data/locations';

// اگر api به‌صورت named export است
import { customersAPI as customersAPINamed } from '../services/api';
// اگر api default باشد
import customersAPIDefault from '../services/api';

/* ---------- Helpers ---------- */
const fa = new Intl.NumberFormat('fa-IR');
const faDate = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleDateString('fa-IR') : '—';
const safe = (v: any, alt: string = '—') =>
    v === undefined || v === null || v === '' ? alt : v;

/* ---------- Types ---------- */
type CustomerVM = {
    id: string | number;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    country: string;
    province: string;
    city: string;
    address: string;
    complexName?: string;
    complexNumber?: string;
    created_at?: string;
};

type LBEntry = {
    agent_id: string | number;
    items_sold: number;
    gross: string | number;
    commission: string | number;
    rnk: string | number;
    first_name?: string;
    last_name?: string;
    full_name?: string;
};
type LeaderboardPayload = {
    period: { days: number };
    top5: LBEntry[];
    self: LBEntry;
    last: LBEntry | null;
};

/* ---------- API Bridge: list / getLeaderboard / addCustomer ---------- */
function buildCustomersApi() {
    const candidate = (customersAPINamed ?? customersAPIDefault) as any;

    // اگر فانکشن برگشته (rare)
    if (typeof candidate === 'function') {
        return {
            async list() {
                return candidate();
            },
            async getLeaderboard(days?: number) {
                if (typeof candidate.getLeaderboard === 'function') return candidate.getLeaderboard(days);
                if (customersAPINamed?.getLeaderboard) return customersAPINamed.getLeaderboard(days);
                if ((customersAPIDefault as any)?.getLeaderboard)
                    return (customersAPIDefault as any).getLeaderboard(days);
                throw new Error('customersAPI.getLeaderboard یافت نشد');
            },
            async addCustomer(data: any) {
                if (typeof candidate.addCustomer === 'function') return candidate.addCustomer(data);
                if (customersAPINamed?.addCustomer) return customersAPINamed.addCustomer(data);
                if ((customersAPIDefault as any)?.addCustomer) return (customersAPIDefault as any).addCustomer(data);
                // نام‌های رایج
                if (customersAPINamed?.create) return customersAPINamed.create(data);
                if ((customersAPIDefault as any)?.create) return (customersAPIDefault as any).create(data);
                if (customersAPINamed?.post) return customersAPINamed.post(data);
                if ((customersAPIDefault as any)?.post) return (customersAPIDefault as any).post(data);
                throw new Error('customersAPI.addCustomer/create/post یافت نشد');
            },
        };
    }

    // اگر آبجکت
    if (candidate && typeof candidate === 'object') {
        const listFn = candidate.list ?? candidate.getCustomers ?? candidate.fetch ?? candidate.index;
        const leaderboardFn =
            candidate.getLeaderboard ?? candidate.leaderboard ?? candidate.fetchLeaderboard;
        const addFn =
            candidate.addCustomer ?? candidate.create ?? candidate.add ?? candidate.post ?? candidate.store;

        return {
            async list() {
                if (typeof listFn !== 'function')
                    throw new Error('customersAPI.list / getCustomers یافت نشد');
                return listFn();
            },
            async getLeaderboard(days?: number) {
                if (typeof leaderboardFn !== 'function')
                    throw new Error('customersAPI.getLeaderboard یافت نشد');
                return leaderboardFn(days);
            },
            async addCustomer(data: any) {
                if (typeof addFn !== 'function')
                    throw new Error('customersAPI.addCustomer/create/post یافت نشد');
                return addFn(data);
            },
        };
    }

    throw new Error('customersAPI is not a function or object');
}

const customersApi = (() => {
    try {
        return buildCustomersApi();
    } catch (e) {
        console.error(e);
        return null;
    }
})();

/* ---------- Normalizers ---------- */
function normalizeCustomers(payload: any): CustomerVM[] {
    const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.customers)
            ? payload.customers
            : [];

    return arr.map((c: any) => ({
        id: c.id ?? c.customer_id ?? c._id ?? Math.random(),
        firstName: c.first_name || c.firstName || '',
        lastName: c.last_name || c.lastName || '',
        phone: c.phone_number || c.phone || '',
        email: c.email || '',
        country: c.country_id || c.country || 'IR',
        province: c.province_id || c.province || '',
        city: c.city_id || c.city || '',
        address: c.address || '',
        complexName: c.complexname || c.complexName || '',
        complexNumber: c.complexnumber || c.complexNumber || '',
        created_at: c.created_at || c.createdAt,
    }));
}

const periodToDays = (
    p: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all',
): number | null => {
    switch (p) {
        case 'day':
            return 'day';
        case 'all':
            return 'all';
        case 'week':
            return 'week';
        case 'month':
            return 'month';
        case 'quarter':
            return 'quarter';
        case 'year':
            return 'year';
        default:
            return 'all';
    }
};

const agentDisplayName = (e: LBEntry) => {
    if (e.full_name) return e.full_name;
    const fn = e.first_name?.trim(),
        ln = e.last_name?.trim();
    if (fn || ln) return `${fn ?? ''} ${ln ?? ''}`.trim();
    return `نماینده #${e.agent_id}`;
};

/* ---------- ساخت Map برای name_native ---------- */
const buildLocMaps = () => {
    const pMap = new Map<string, any>();
    const cMap = new Map<string, any>();
    for (const country of locationData.countries) {
        for (const prov of country.provinces) {
            pMap.set(prov.id, prov);
            for (const city of prov.cities) {
                cMap.set(city.id, city);
            }
        }
    }
    return { provinceMap: pMap, cityMap: cMap };
};
const { provinceMap, cityMap } = buildLocMaps();

const getNativeLocation = (provinceId?: string, cityId?: string) => {
    const prov = provinceId ? provinceMap.get(provinceId) : undefined;
    const ct = cityId ? cityMap.get(cityId) : undefined;
    const provName = prov?.name_native || prov?.name || '';
    const cityName = ct?.name_native || ct?.name || '';
    if (!provName && !cityName) return '—';
    if (provName && cityName) return `${cityName}، ${provName}`;
    return cityName || provName || '—';
};

/* ---------- Component ---------- */
const Customers: React.FC = () => {
    const [customers, setCustomers] = useState<CustomerVM[]>([]);
    const [leaderboard, setLeaderboard] = useState<LeaderboardPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [lbLoading, setLbLoading] = useState(true);
    const [error, setError] = useState('');
    const [lbError, setLbError] = useState('');

    // Dialog + Form
    const [dialogOpen, setDialogOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: '',
        complexNumber: '',
        complexName: '',
    });
    const [selectedCountry, setSelectedCountry] = useState('IR');
    const [selectedProvince, setSelectedProvince] = useState('');
    const [selectedCity, setSelectedCity] = useState('');

    // فیلترها
    const [filterOpen, setFilterOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [fCountry, setFCountry] = useState('IR');
    const [fProvince, setFProvince] = useState('');
    const [fCity, setFCity] = useState('');

    // پجینیشن
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // لیدربورد
    const [period, setPeriod] = useState<
        'day' | 'week' | 'month' | 'quarter' | 'year' | 'all'
        >('month');

    // totals برای هدر
    const totals = useMemo(
        () => ({
            count: customers.length,
        }),
        [customers],
    );

    useEffect(() => {
        (async () => {
            try {
                if (!customersApi) throw new Error('customersAPI resolve نشد');
                const res = await customersApi.list();
                setCustomers(normalizeCustomers(res));
            } catch (err: any) {
                console.error('Error fetching customers', err);
                setError(err?.message || 'خطا در دریافت مشتری‌ها');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            setLbLoading(true);
            setLbError('');
            try {
                if (!customersApi) throw new Error('customersAPI resolve نشد');
                const days = periodToDays(period) ?? undefined;
                const data = await customersApi.getLeaderboard(days as any);
                setLeaderboard(data as unknown as LeaderboardPayload);
            } catch (err: any) {
                console.error('Error fetching leaderboard', err);
                setLbError(err?.message || 'خطا در دریافت لیدربورد');
            } finally {
                setLbLoading(false);
            }
        })();
    }, [period]);

    // جستجو/فیلتر
    const filtered = useMemo(() => {
        const q = search.trim();
        return customers.filter((c) => {
            const matchText =
                !q ||
                [c.firstName, c.lastName, c.phone, c.email, c.address, c.complexName, c.complexNumber]
                    .filter(Boolean)
                    .some((t) => String(t).includes(q));
            const matchCountry = !fCountry || c.country === fCountry;
            const matchProv = !fProvince || c.province === fProvince;
            const matchCity = !fCity || c.city === fCity;
            return matchText && matchCountry && matchProv && matchCity;
        });
    }, [customers, search, fCountry, fProvince, fCity]);

    // پجینیشن
    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);
    const paged = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    // handlers - Add Customer
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddCustomer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!customersApi) return toast.error('سرویس مشتریان در دسترس نیست');

        // FormData برای حداکثر سازگاری: هم camelCase هم snake_case
        const fd = new FormData();
        const entries: Record<string, string> = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
            complexName: formData.complexName,
            complexNumber: formData.complexNumber,
            countryId: selectedCountry,
            province: selectedProvince,
            city: selectedCity,
            // snake_case پرکاربرد بک‌اند‌ها:
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone_number: formData.phone,
            complexname: formData.complexName,
            complexnumber: formData.complexNumber,
            country_id: selectedCountry,
            province_id: selectedProvince,
            city_id: selectedCity,
        };
        Object.entries(entries).forEach(([k, v]) => fd.append(k, v ?? ''));

        setAddLoading(true);
        try {
            await customersApi.addCustomer(fd);
            toast.success('مشتری با موفقیت افزوده شد');
            setDialogOpen(false);
            setFormData({
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                address: '',
                complexNumber: '',
                complexName: '',
            });
            setSelectedCountry('IR');
            setSelectedProvince('');
            setSelectedCity('');
            // refresh
            const res = await customersApi.list();
            setCustomers(normalizeCustomers(res));
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'افزودن مشتری ناموفق بود';
            toast.error(msg);
        } finally {
            setAddLoading(false);
        }
    };

    // UI State
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">در حال بارگذاری...</p>
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <Alert variant="destructive" dir="rtl" className="m-4">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    // آیکون رتبه
    const getRankIcon = (rank: number) => {
        switch (rank) {
            case 1:
                return <Crown className="h-6 w-6 text-yellow-500" />;
            case 2:
                return <Trophy className="h-6 w-6 text-gray-400" />;
            case 3:
                return <Medal className="h-6 w-6 text-amber-600" />;
            default:
                return <Award className="h-6 w-6 text-green-500" />;
        }
    };
    const formatCurrency = (amount: number | string) =>
        `${fa.format(Number((amount as any) / 10 || 0))} تومان`;

    return (
        <div
            className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50"
            dir="rtl"
        >
            {/* هدر اصلی مثل Licenses */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-8 shadow-2xl">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold">
                                مشتریان و لیدربورد
                            </h1>
                        </div>
                        <p className="text-green-100 text-lg">
                            مدیریت مشتریان و مشاهدهٔ رتبه‌بندی عملکرد
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30">
                            <div className="text-sm opacity-90">کل مشتریان</div>
                            <div className="text-2xl font-bold text-green-200">
                                {fa.format(totals.count)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* اکشن‌ها: فیلتر + افزودن مشتری */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4">
                <div className="flex-1" />
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        className="border-green-300 text-green-700 w-full sm:w-auto bg-white/80 hover:bg-green-50"
                        onClick={() => setFilterOpen((v) => !v)}
                    >
                        <Filter className="ml-2 h-4 w-4" />
                        فیلتر
                    </Button>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto shadow-md">
                                <Plus className="ml-2 h-4 w-4" />
                                افزودن مشتری
                            </Button>
                        </DialogTrigger>
                        <DialogContent
                            className="max-w-2xl max-h-[90vh] overflow-y-auto"
                            dir="rtl"
                        >
                            <DialogHeader>
                                <DialogTitle className="text-green-900">
                                    افزودن مشتری جدید
                                </DialogTitle>
                                <DialogDescription>
                                    فرم زیر را تکمیل کنید
                                </DialogDescription>
                            </DialogHeader>

                            {/* فرم افزودن مشتری */}
                            <form
                                onSubmit={handleAddCustomer}
                                className="space-y-4"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm">نام</label>
                                        <Input
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm">
                                            نام خانوادگی
                                        </label>
                                        <Input
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm">موبایل</label>
                                        <Input
                                            name="phone"
                                            type="tel"
                                            dir="ltr"
                                            placeholder="09xxxxxxxxx"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm">ایمیل (اختیاری)</label>
                                        <Input
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>

                                <LocationSelector
                                    selectedCountry={selectedCountry}
                                    selectedProvince={selectedProvince}
                                    selectedCity={selectedCity}
                                    onCountryChange={setSelectedCountry}
                                    onProvinceChange={setSelectedProvince}
                                    onCityChange={setSelectedCity}
                                />

                                <div className="space-y-2">
                                    <label className="text-sm">آدرس</label>
                                    <Input
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm">
                                            نام مجتمع
                                        </label>
                                        <Input
                                            name="complexName"
                                            value={formData.complexName}
                                            onChange={handleInputChange}
                                            required

                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm">
                                            شماره/تماس مجتمع
                                        </label>
                                        <Input
                                            name="complexNumber"
                                            value={formData.complexNumber}
                                            onChange={handleInputChange}
                                            required

                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={addLoading}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                                >
                                    {addLoading ? (
                                        <>
                                            <Loader2 className="ml-2 h-4 w-4 animate-spin" />{' '}
                                            در حال افزودن…
                                        </>
                                    ) : (
                                        'افزودن مشتری'
                                    )}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* فیلترهای مشتریان با استایل جدید */}
            {filterOpen && (
                <Card className="border-green-200/50 shadow-lg bg-white/80 backdrop-blur-sm">
                    <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-green-600 flex items-center justify-center">
                                <Filter className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-green-900 text-base">
                                    فیلتر مشتریان
                                </CardTitle>
                                <CardDescription className="text-green-700 text-sm">
                                    جستجو و فیلتر بر اساس موقعیت
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <label className="text-sm text-green-900 font-medium">
                                    جستجو (نام/موبایل/ایمیل/آدرس)
                                </label>
                                <Input
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                    placeholder="مثال: 0912 یا علی"
                                    className="h-11 border-green-200 focus:ring-green-500 focus:border-green-500"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <LocationSelector
                                    selectedCountry={fCountry}
                                    selectedProvince={fProvince}
                                    selectedCity={fCity}
                                    onCountryChange={(v) => {
                                        setFCountry(v);
                                        setPage(1);
                                    }}
                                    onProvinceChange={(v) => {
                                        setFProvince(v);
                                        setPage(1);
                                    }}
                                    onCityChange={(v) => {
                                        setFCity(v);
                                        setPage(1);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-1">
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => {
                                    setSearch('');
                                    setFCountry('IR');
                                    setFProvince('');
                                    setFCity('');
                                    setPage(1);
                                }}
                            >
                                پاک‌سازی
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* لیدربورد */}
            <Card className="border-green-200/60 bg-white/90 backdrop-blur-sm shadow-lg">
                <CardHeader className="pb-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <CardTitle className="text-green-900">
                                لیدربورد نمایندگان
                            </CardTitle>
                            <CardDescription className="text-green-700">
                                بازه:{' '}
                                {period === 'all'
                                    ? 'همه'
                                    : period === 'day'
                                        ? 'روز'
                                        : period === 'week'
                                            ? 'هفته'
                                            : period === 'month'
                                                ? 'ماه'
                                                : period === 'quarter'
                                                    ? 'فصل'
                                                    : 'سال'}
                            </CardDescription>
                        </div>
                        <div className="w-full md:w-auto">
                            <PeriodPicker
                                value={period}
                                onChange={(p) => setPeriod(p)}
                                disabled={lbLoading}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {lbLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                        </div>
                    ) : lbError ? (
                        <Alert variant="destructive" dir="rtl">
                            <AlertDescription>{lbError}</AlertDescription>
                        </Alert>
                    ) : !leaderboard ? (
                        <div className="py-6 text-center text-green-700">
                            داده‌ای برای نمایش لیدربورد موجود نیست
                        </div>
                    ) : (
                        <>
                            {/* Summary cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-lg flex items-center">
                                            <Trophy className="ml-2 h-5 w-5" /> رتبهٔ
                                            شما
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-bold">
                                            #{leaderboard?.self.rnk}
                                        </div>
                                        <p className="text-emerald-100">
                                            موقعیت فعلی
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-lg flex items-center">
                                            <Award className="ml-2 h-5 w-5" />{' '}
                                            تعداد فروش
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-3xl font-bold">
                                            {fa.format(
                                                Number(
                                                    leaderboard?.self.items_sold ||
                                                    0,
                                                ),
                                            )}
                                        </div>
                                        <p className="text-emerald-100">
                                            در بازهٔ انتخابی
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-lg flex items-center">
                                            <Medal className="ml-2 h-5 w-5" />{' '}
                                            کمیسیون
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatCurrency(
                                                leaderboard?.self.commission ||
                                                0,
                                            )}
                                        </div>
                                        <p className="text-cyan-100">
                                            در بازهٔ انتخابی
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Top 5 */}
                            <Card className="bg-white/80 backdrop-blur-sm border-green-200">
                                <CardHeader className="px-3 py-4 sm:px-6 sm:py-4">
                                    <CardTitle className="text-green-900 flex items-center text-lg sm:text-xl">
                                        <Trophy className="ml-2 h-5 w-5 sm:h-6 sm:w-6 text-yellow-500" />
                                        برترین‌ها{' '}
                                        {leaderboard?.period?.days
                                            ? `– ${
                                                leaderboard?.period.days
                                            } روز اخیر`
                                            : ''}
                                    </CardTitle>
                                    <CardDescription className="text-green-700 text-sm sm:text-base mt-1">
                                        نمایندگان برتر بر اساس فروش
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="px-3 py-4 sm:px-6 sm:py-4">
                                    {!leaderboard?.top5 ||
                                    leaderboard.top5.length === 0 ? (
                                        <div className="text-center py-8 text-green-700">
                                            موردی برای نمایش نیست
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                                            {leaderboard.top5.map((entry) => {
                                                const isSelf =
                                                    String(entry.agent_id) ===
                                                    String(
                                                        leaderboard.self
                                                            .agent_id,
                                                    );
                                                const rnk = Number(entry.rnk);
                                                return (
                                                    <div
                                                        key={`top-${entry.agent_id}`}
                                                        className={`rounded-lg sm:rounded-xl border p-3 sm:p-4 transition-all ${
                                                            isSelf
                                                                ? 'bg-green-100 border-green-500'
                                                                : 'bg-gray-50 hover:bg-green-50 border-green-100'
                                                        } flex flex-col gap-3`}
                                                    >
                                                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-full bg-gradient-to-r from-green-400 to-emerald-500">
                                                                {getRankIcon(
                                                                    rnk,
                                                                )}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-green-900 truncate text-sm sm:text-base flex items-center gap-2">
                                                                    {agentDisplayName(
                                                                        entry,
                                                                    )}
                                                                    {isSelf && (
                                                                        <Badge className="bg-green-600 text-white text-xs whitespace-nowrap">
                                                                            شما
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-green-600">
                                                                    رتبه #{rnk}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-2 sm:gap-3 bg-white/50 rounded p-2 sm:p-3">
                                                            <div className="text-right">
                                                                <div className="text-xs text-green-600 font-medium">
                                                                    فروش
                                                                </div>
                                                                <div className="text-sm sm:text-base text-green-900 font-semibold">
                                                                    {fa.format(
                                                                        Number(
                                                                            entry.items_sold,
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="text-xs text-green-600 font-medium">
                                                                    مبلغ
                                                                </div>
                                                                <div className="text-sm sm:text-base text-green-900 font-semibold">
                                                                    {fa.format(
                                                                        Number(
                                                                            (entry.gross as any) /
                                                                            10 ||
                                                                            0,
                                                                        ),
                                                                    )}{' '}
                                                                    تومان
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <Badge
                                                            variant="outline"
                                                            className="border-green-300 text-green-700 text-xs sm:text-sm justify-center py-2"
                                                        >
                                                            کمیسیون:{' '}
                                                            {fa.format(
                                                                Number(
                                                                    (entry.commission as any) /
                                                                    10 ||
                                                                    0,
                                                                ),
                                                            )}{' '}
                                                            تومان
                                                        </Badge>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* آخرین نفر */}
                            {(() => {
                                if (!leaderboard?.last) return null;
                                const shown = new Set([
                                    ...leaderboard.top5.map((x) =>
                                        String(x.agent_id),
                                    ),
                                    String(leaderboard.self.agent_id),
                                ]);
                                if (
                                    shown.has(
                                        String(leaderboard.last.agent_id),
                                    )
                                )
                                    return null;
                                const row = leaderboard.last;
                                const rnk = Number(row.rnk);
                                return (
                                    <Card className="mt-4 sm:mt-6 border-green-200 bg-white/80 backdrop-blur-sm">
                                        <CardHeader className="px-3 py-4 sm:px-6 sm:py-4">
                                            <CardTitle className="text-green-900 text-lg sm:text-xl">
                                                آخرین رتبه
                                            </CardTitle>
                                            <CardDescription className="text-green-700 text-sm sm:text-base">
                                                کمترین فروش در بازه
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="px-3 py-4 sm:px-6 sm:py-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gray-50">
                                                <div className="font-semibold text-green-900 text-sm sm:text-base flex-1 min-w-0 truncate">
                                                    {agentDisplayName(row)}
                                                </div>
                                                <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                                                    <div className="text-green-700">
                                                        رتبه #{rnk}
                                                    </div>
                                                    <div className="text-green-700">
                                                        {fa.format(
                                                            Number(
                                                                row.items_sold,
                                                            ),
                                                        )}{' '}
                                                        فروش
                                                    </div>
                                                    <div className="text-green-700 whitespace-nowrap">
                                                        {formatCurrency(
                                                            row.commission,
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })()}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* فهرست مشتریان */}
            <Card className="bg-white/80 backdrop-blur-sm border-green-200 shadow-lg">
                <CardHeader className="pb-3">
                    <CardTitle className="text-green-900">
                        فهرست مشتریان
                    </CardTitle>
                    <CardDescription className="text-green-700">
                        {fa.format(totalItems)} مشتری یافت شد
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* موبایل: کارت‌ها */}
                    <div className="grid gap-2 sm:hidden">
                        {paged.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="h-12 w-12 mx-auto mb-4 text-green-300" />
                                <p className="text-green-600">
                                    مشتری‌ای یافت نشد
                                </p>
                            </div>
                        ) : (
                            paged.map((c) => (
                                <div
                                    key={c.id}
                                    className="rounded-lg border border-emerald-600/60 p-3 bg-white"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-semibold text-green-900 truncate max-w-[65%]">
                                            {safe(
                                                `${c.firstName} ${c.lastName}`.trim(),
                                            )}
                                        </div>
                                        <Badge
                                            variant="outline"
                                            className="border-green-300 text-green-700 shrink-0"
                                        >
                                            {faDate(c.created_at)}
                                        </Badge>
                                    </div>
                                    <div className="mt-1.5 text-[13px] leading-5 text-green-800 space-y-0.5">
                                        <div
                                            dir="ltr"
                                            className="font-mono truncate"
                                        >
                                            {safe(c.phone)}
                                        </div>
                                        <div className="truncate">
                                            {safe(c.email)}
                                        </div>
                                        <div className="text-green-700 truncate">
                                            {getNativeLocation(
                                                c.province,
                                                c.city,
                                            )}
                                        </div>
                                        {c.complexName && (
                                            <div className="text-green-700 truncate">
                                                مجتمع: {c.complexName}
                                            </div>
                                        )}
                                        {c.address && (
                                            <div className="text-gray-600 line-clamp-2">
                                                آدرس: {c.address}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* دسکتاپ: جدول */}
                    <div className="hidden sm:block overflow-x-auto">
                        {paged.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="h-12 w-12 mx-auto mb-4 text-green-300" />
                                <p className="text-green-600">
                                    مشتری‌ای یافت نشد
                                </p>
                            </div>
                        ) : (
                            <Table className="min-w-[720px]">
                                <TableHeader className="sticky top-0 z-10 bg-white">
                                    <TableRow className="border-emerald-600/60">
                                        <TableHead className="text-right text-green-900">
                                            نام
                                        </TableHead>
                                        <TableHead className="text-right text-green-900">
                                            موبایل
                                        </TableHead>
                                        <TableHead className="text-right text-green-900 hidden md:table-cell">
                                            ایمیل
                                        </TableHead>
                                        <TableHead className="text-right text-green-900 hidden lg:table-cell">
                                            موقعیت
                                        </TableHead>
                                        <TableHead className="text-right text-green-900">
                                            تاریخ ثبت
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paged.map((c) => (
                                        <TableRow
                                            key={c.id}
                                            className="border-emerald-600/60 hover:bg-green-50"
                                        >
                                            <TableCell className="font-medium text-green-900">
                                                {safe(
                                                    `${c.firstName} ${c.lastName}`.trim(),
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className="text-green-700"
                                                dir="ltr"
                                            >
                                                {safe(c.phone)}
                                            </TableCell>
                                            <TableCell className="text-green-700 hidden md:table-cell">
                                                {safe(c.email)}
                                            </TableCell>
                                            <TableCell className="text-green-700 hidden lg:table-cell">
                                                {getNativeLocation(
                                                    c.province,
                                                    c.city,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-green-700">
                                                {faDate(c.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    {/* کنترل‌های صفحه‌بندی */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                                آیتم در هر صفحه:
                            </span>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                className="h-9 rounded-md border border-green-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                            >
                                <option value={5}>۵</option>
                                <option value={10}>۱۰</option>
                                <option value={20}>۲۰</option>
                                <option value={50}>۵۰</option>
                            </select>
                        </div>

                        <div className="text-sm text-gray-700 text-center bg-green-50 px-4 py-2 rounded-lg">
                            صفحه{' '}
                            <span className="text-green-700 font-bold">
                                {fa.format(page)}
                            </span>{' '}
                            از{' '}
                            <span className="text-green-700 font-bold">
                                {fa.format(totalPages)}
                            </span>{' '}
                            — مجموع{' '}
                            <span className="text-green-700 font-bold">
                                {fa.format(totalItems)}
                            </span>{' '}
                            مشتری
                        </div>

                        <div className="flex w-full sm:w-auto items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setPage((p) => Math.max(1, p - 1))
                                }
                                disabled={page <= 1}
                                className="flex-1 sm:flex-none border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                                <ChevronRight className="ml-1 h-4 w-4" />
                                قبلی
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setPage((p) =>
                                        Math.min(totalPages, p + 1),
                                    )
                                }
                                disabled={page >= totalPages}
                                className="flex-1 sm:flex-none border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                                بعدی
                                <ChevronLeft className="mr-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Customers;
